/**
 * Base Input Component
 * 
 * Text/number input with label, error state, and helper text.
 * Meets WCAG 2.1 AA and 44px touch targets.
 */

import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  error?: string;
  helper?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helper, prefix, suffix, className = '', id, ...props }, ref) => {
    const inputId = id || `input-${label?.toLowerCase().replace(/\s/g, '-')}`;
    const hasError = Boolean(error);
    
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="label">
            {label}
          </label>
        )}
        
        <div className="relative">
          {prefix && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400 pointer-events-none">
              {prefix}
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            className={`
              input
              ${prefix ? 'pl-8' : ''}
              ${suffix ? 'pr-12' : ''}
              ${hasError ? 'input-error' : ''}
              ${className}
            `}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${inputId}-error` : helper ? `${inputId}-helper` : undefined
            }
            {...props}
          />
          
          {suffix && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400">
              {suffix}
            </div>
          )}
        </div>
        
        {error && (
          <p id={`${inputId}-error`} className="error-text" role="alert">
            {error}
          </p>
        )}
        
        {helper && !error && (
          <p id={`${inputId}-helper`} className="helper-text">
            {helper}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
